// Attribution for the قرعه‌کشی landing: one code per person we invited.
//
// The campaign works by handing every recipient their own link. Two shapes
// carry the same code and land on the same page:
//
//   mini app   eitaa.com/hamyan_minibot_app/start?startapp=q_<code>
//   plain web  pwa.hamyanapp.com/q/<code>
//
// The mini-app form is the one that matters — inside Eitaa the person can be
// signed in from their messenger contact without an SMS — but a link shared
// out of the messenger has to keep working, so both exist and both resolve to
// `/q/<code>`.
//
// What the code buys us: it becomes a PostHog **super-property**, so it rides
// on every later event this browser sends, including `login_completed` and
// `fund_created` fired days afterwards. That is what turns "someone opened the
// landing" into "this specific person we invited went on to build a صندوق".
//
// The code is a campaign id, not a credential. It grants nothing, it is
// visible in the URL, and a person who forwards their link simply attributes
// their friend to themselves. Treat it accordingly: never gate anything on it.

import { getStartParam, isMiniapp } from 'modules/miniapp/detect'
import { queueRegister } from 'utilities/analytics'

export const LANDING_CAMPAIGN = 'qorekeshi'

/**
 * What marks a start param as ours.
 *
 * `startapp` is a single shared slot — anything else we ever deep-link into
 * the mini app has to fit through it too — so the landing claims a prefix
 * rather than the whole namespace.
 */
export const LANDING_START_PARAM_PREFIX = 'q_'

/** Where a code is remembered between visits. */
const CODE_KEY = 'landing:code'
const ENTRY_KEY = 'landing:entry'
const FIRST_SEEN_KEY = 'landing:first_seen'

export type LandingEntry = 'miniapp' | 'web'

/**
 * Codes are generated by us, but they arrive from a URL, so they are treated
 * as hostile input: anything outside this alphabet is discarded rather than
 * sanitised, and it never reaches the DOM or a request path either way.
 */
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/

export const normalizeLandingCode = (raw?: string | null): string | null => {
    if (!raw) return null
    const trimmed = raw.trim()
    return CODE_PATTERN.test(trimmed) ? trimmed : null
}

/** `/q/<code>` — the one path both link shapes end up on. */
export const landingPath = (code?: string | null): string => {
    const safe = normalizeLandingCode(code)
    return safe ? `/q/${safe}` : '/q'
}

/**
 * The code this launch carries, if it is a landing launch.
 *
 * Returns null for an ordinary mini-app open and for every other start param,
 * so the caller can use it as "should we route to the landing at all".
 */
export const landingCodeFromStartParam = (): string | null => {
    const param = getStartParam()
    if (!param || !param.startsWith(LANDING_START_PARAM_PREFIX)) return null
    return normalizeLandingCode(param.slice(LANDING_START_PARAM_PREFIX.length))
}

const read = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

const write = (key: string, value: string) => {
    try {
        window.localStorage.setItem(key, value)
    } catch {
        /* private-mode webview — PostHog's own persistence still carries it */
    }
}

export interface LandingAttribution {
    landing_campaign: string
    landing_code: string
    landing_entry: LandingEntry
    landing_first_seen: string
}

const attribution = (
    code: string,
    entry: LandingEntry,
    firstSeen: string
): LandingAttribution => ({
    landing_campaign: LANDING_CAMPAIGN,
    landing_code: code,
    landing_entry: entry,
    landing_first_seen: firstSeen,
})

/**
 * Record which invitation this browser came in on, and attach it to every
 * event from here on.
 *
 * `first_seen` is written once and never overwritten: the question the
 * campaign asks is "when did this person first arrive", and a returning
 * visitor re-opening their link should not reset that. The code itself *is*
 * overwritten — if somebody arrives on a second, different invitation, the
 * newer one is the one that brought them back.
 *
 * `queueRegister` rather than `posthog.register`: this runs from
 * `bootstrapMiniapp`, which is before `<PostHogProvider>` has called `init()`,
 * where a direct call is a silent no-op.
 */
export const rememberLandingAttribution = (
    code: string,
    entry: LandingEntry = isMiniapp() ? 'miniapp' : 'web'
): LandingAttribution => {
    const firstSeen = read(FIRST_SEEN_KEY) || new Date().toISOString()

    write(CODE_KEY, code)
    write(ENTRY_KEY, entry)
    write(FIRST_SEEN_KEY, firstSeen)

    const props = attribution(code, entry, firstSeen)
    queueRegister({ ...props })
    return props
}

export const getLandingAttribution = (): LandingAttribution | null => {
    const code = normalizeLandingCode(read(CODE_KEY))
    if (!code) return null
    const entry = read(ENTRY_KEY) === 'miniapp' ? 'miniapp' : 'web'
    return attribution(code, entry, read(FIRST_SEEN_KEY) || '')
}

/**
 * Re-attach a previously recorded invitation at startup.
 *
 * PostHog persists super-properties itself, so on most launches this is
 * redundant — but not on the ones that matter. A mini-app open is a brand-new
 * webview, `logout()` calls `localStorage.clear()`, and a blocked or slow
 * PostHog load can leave the SDK with nothing. Re-registering from our own key
 * costs one read and keeps the attribution alive across all three.
 *
 * Call before the first render, from the same place the session is settled.
 */
export const restoreLandingAttribution = (): void => {
    const stored = getLandingAttribution()
    if (stored) queueRegister({ ...stored })
}
